/**
 * Arranque do nucleo.
 *
 * A ordem aqui nao e arbitraria e e a parte mais fragil do sistema:
 *
 *   1. registro/eventos/sessao — sem I/O, para os plugins poderem ser aceites;
 *   2. plugins declaram entidades (ainda nao correm);
 *   3. so entao a base de dados abre, ja com o esquema completo (o IndexedDB
 *      nao aceita tabelas novas depois de aberta);
 *   4. configuracoes carregam e aplicam a configuracao regional;
 *   5. repositorios e auditoria, que dependem da base de dados;
 *   6. `register()` de cada plugin, que ja pode pedir repositorios.
 */
import {
  CoreEvents,
  type BaseRecord,
  type FlosPlugin,
  type NotifierLike,
  type PermissionSet,
  type SessionInfo,
} from '@flos/shared';
import { AuditService } from './audit';
import { DIAGNOSTICS_META_KEY, SchemaRegistry, openDatabase, type FlosDatabase } from './db';
import { EventBus } from './events';
import { FlosLogger, createLogger, type LogLevel, type LoggerSink } from './logger';
import {
  PluginRegistry,
  createFallbackNotifier,
  type BootReport,
  type PluginRegistryDeps,
} from './registry';
import {
  RepositoryFactory,
  ValidatorRegistry,
  type Repository,
  type RepositoryDeps,
} from './repository';
import { SessionService } from './session';
import { SettingsService } from './settings';

export interface CreateCoreOptions {
  /** Plugins a carregar, por ordem de declaracao (as dependencias reordenam). */
  plugins?: readonly FlosPlugin[];
  databaseName?: string;
  logLevel?: LogLevel;
  logSink?: LoggerSink;
  /** Substituido por `@flos/notifications` quando esse pacote estiver ligado. */
  notifier?: NotifierLike;
  /** Sessao inicial. Sem ela, ler ou escrever dados lanca `NoSessionError`. */
  session?: { info: SessionInfo; permissions?: PermissionSet };
  /** Relogio injetavel — torna os testes deterministas. */
  now?: () => string;
  /** Por padrão arrancamos os plugins de imediato. */
  autoStart?: boolean;
}

export interface FlosCore {
  db: FlosDatabase;
  events: EventBus;
  logger: FlosLogger;
  schema: SchemaRegistry;
  session: SessionService;
  settings: SettingsService;
  audit: AuditService;
  registry: PluginRegistry;
  repositories: RepositoryFactory;
  validators: ValidatorRegistry;
  /** Atalho para `repositories.for()`. */
  repository<T extends BaseRecord>(entity: string): Repository<T>;
  /** Arranca os plugins (ja chamado se `autoStart` nao foi desligado). */
  start(): Promise<BootReport>;
  /** Relatorio do arranque, `null` enquanto nao arrancou. */
  boot: BootReport | null;
  /**
   * Troca o servico de notificacoes depois do arranque.
   *
   * Existe porque as duas coisas sao verdade ao mesmo tempo: os plugins tem de
   * estar registrados antes de a base de dados abrir (caminho critico), e o
   * `@flos/notifications` so e carregado depois do primeiro pintado. Sem isto,
   * os plugins ficavam para sempre com o notificador de reserva que apenas
   * escreve no diagnostico — os lembretes da medicacao nunca chegariam.
   */
  useNotifier(next: NotifierLike): void;
  destroy(): Promise<void>;
}

export async function createCore(options: CreateCoreOptions = {}): Promise<FlosCore> {
  const logger = createLogger({
    level: options.logLevel ?? 'info',
    ...(options.logSink ? { sink: options.logSink } : {}),
  });
  const events = new EventBus({ logger });
  const schema = new SchemaRegistry(logger);
  const session = new SessionService(events);
  const validators = new ValidatorRegistry();

  if (options.session) {
    session.set(options.session.info, options.session.permissions);
  }

  let settings: SettingsService | null = null;
  let repositories: RepositoryFactory | null = null;
  // O notificador que os plugins recebem e um delegador, e nunca muda de
  // identidade. Quem muda e o destino: `registry` entrega `deps.notifier()` a
  // cada plugin no momento do registro, portanto trocar a variavel depois nao
  // chegaria a ninguem — trocamos o interior, nao o invólucro.
  const fallbackNotifier = createFallbackNotifier(logger);
  let currentNotifier: NotifierLike = options.notifier ?? fallbackNotifier;
  const notifier: NotifierLike = {
    local: (input) => currentNotifier.local(input),
    family: (input) => currentNotifier.family(input),
  };

  const registryDeps: PluginRegistryDeps = {
    logger,
    events,
    schema,
    session,
    // Ligacao tardia: estes servicos so existem depois de a base de dados abrir,
    // mas os plugins ja tem de estar registrados antes disso.
    repositories: () => {
      if (!repositories) throw new Error('Os repositorios ainda nao estao disponiveis.');
      return repositories;
    },
    settings: () => {
      if (!settings) throw new Error('As configuracoes ainda nao estao disponiveis.');
      return settings;
    },
    notifier: () => notifier,
  };
  const registry = new PluginRegistry(registryDeps);

  registry.registerAll(options.plugins ?? []);

  const db = await openDatabase({
    schema,
    logger,
    ...(options.databaseName ? { name: options.databaseName } : {}),
  });

  settings = new SettingsService(db, events, logger);
  await settings.load();

  const repositoryDeps: RepositoryDeps = {
    db,
    events,
    logger,
    session,
    validators,
    ...(options.now ? { now: options.now } : {}),
  };
  repositories = new RepositoryFactory(repositoryDeps);
  const audit = new AuditService(db, session);

  // O diagnostico so vale se sobreviver ao fecho do aplicativo: gravamos o
  // buffer em `_meta` sempre que ha um erro.
  logger.setPersistence(async (entries) => {
    await db.setMeta(DIAGNOSTICS_META_KEY, entries);
  });

  const core: FlosCore = {
    db,
    events,
    logger,
    schema,
    session,
    settings,
    audit,
    registry,
    repositories,
    validators,
    boot: null,
    repository<T extends BaseRecord>(entity: string): Repository<T> {
      return repositories!.for<T>(entity);
    },
    async start(): Promise<BootReport> {
      const report = await registry.start();
      core.boot = report;
      events.emit(CoreEvents.READY, report);
      return report;
    },
    useNotifier(next: NotifierLike): void {
      currentNotifier = next;
      logger.debug('Servico de notificacoes ligado');
    },
    async destroy(): Promise<void> {
      logger.setPersistence(null);
      events.clear();
      db.close();
    },
  };

  if (options.autoStart !== false) {
    await core.start();
  }
  return core;
}
